<template>
  <div class="rule">
    <div class="ins-seg" role="radiogroup" aria-label="قانون اعتبارسنجی">
      <button v-for="mode in modes" :key="mode.key" type="button" role="radio" :aria-checked="rule.mode === mode.key ? 'true' : 'false'"
              :class="{ active: rule.mode === mode.key }" @click="setMode(mode.key)">{{ mode.label }}</button>
    </div>

    <p v-if="rule.mode === 'none'" class="ins-note">{{ capabilities.length ? 'پاسخ بدون محدودیت پذیرفته می‌شود.' : 'این نوع پاسخ در اپ قانون جداگانه‌ای ندارد؛ اجباری بودن از تنظیم پرسش می‌آید.' }}</p>

    <template v-else-if="rule.mode === 'preset'">
      <label class="ins-field"><span>قانون آماده</span>
        <select v-model.number="rule.preset" @change="loadPreset">
          <option :value="null" disabled>یک قانون را انتخاب کنید</option>
          <option v-for="item in presets" :key="item.id" :value="item.id">{{ item.name }}{{ item.uses ? ` · ${fa(item.uses)} کاربرد` : '' }}</option>
        </select>
      </label>
      <ul v-if="presetSummary.length" class="ins-summary">
        <li v-for="line in presetSummary" :key="line">{{ line }}</li>
      </ul>
      <p v-if="presetIgnored" class="ins-note warn">بخشی از این قانون برای «{{ kindLabel }}» در اپ اعمال نمی‌شود.</p>
      <div class="ins-row">
        <button type="button" class="ins-link" :disabled="!rule.preset" @click="customize">ساخت نسخه‌ی اختصاصی از این قانون</button>
        <router-link to="/designer/rules" class="ins-link" target="_blank">کتابخانه‌ی قوانین</router-link>
      </div>
    </template>

    <template v-else>
      <div v-if="has('value')" class="ins-grid">
        <label class="ins-field"><span>{{ kind === 'Price' ? 'کمترین مبلغ' : 'کمترین عدد' }}</span><input v-model.number="fields.min_value" type="number" inputmode="numeric" dir="ltr" /></label>
        <label class="ins-field"><span>{{ kind === 'Price' ? 'بیشترین مبلغ' : 'بیشترین عدد' }}</span><input v-model.number="fields.max_value" type="number" inputmode="numeric" dir="ltr" /></label>
        <label class="ins-field wide"><span>پیام خطا</span><input v-model="fields.value_error_msg_fa" :placeholder="valuePlaceholder" /></label>
      </div>
      <div v-if="has('length')" class="ins-grid">
        <label class="ins-field"><span>کمترین نویسه</span><input v-model.number="fields.min_lenght" type="number" min="0" dir="ltr" /></label>
        <label class="ins-field"><span>بیشترین نویسه</span><input v-model.number="fields.max_lenght" type="number" min="0" dir="ltr" /></label>
        <label class="ins-field wide"><span>پیام خطای طول</span><input v-model="fields.lenght_error_msg_fa" placeholder="مثلاً: دست‌کم ۱۰ نویسه بنویسید." /></label>
      </div>
      <div v-if="has('regex')" class="ins-grid">
        <div class="ins-field wide">
          <span>الگوی پاسخ (عبارت باقاعده)</span>
          <div class="ins-chips">
            <button v-for="item in regexPresets" :key="item.label" type="button" :class="{ active: fields.regex === item.regex }" @click="useRegex(item)">{{ item.label }}</button>
          </div>
          <input v-model="fields.regex" dir="ltr" class="vn-mono" placeholder="^\d{10}$" :aria-invalid="regexError ? 'true' : 'false'" />
          <small v-if="regexError" class="ins-error">{{ regexError }}</small>
        </div>
        <label class="ins-field wide"><span>پیام خطای الگو</span><input v-model="fields.regex_error_msg_fa" placeholder="مثلاً: کد ملی باید ده رقم باشد." /></label>
        <label v-if="fields.regex && !regexError" class="ins-field wide tester"><span>آزمایش الگو</span>
          <input v-model="sample" placeholder="یک نمونه پاسخ بنویسید" />
          <small v-if="sample" :class="sampleOk ? 'ok' : 'ins-error'">{{ sampleOk ? 'این پاسخ پذیرفته می‌شود.' : (fields.regex_error_msg_fa || 'این پاسخ پذیرفته نمی‌شود.') }}</small>
        </label>
      </div>
      <div v-if="has('count')" class="ins-grid">
        <label class="ins-field"><span>کمترین انتخاب</span><input v-model.number="fields.min_choice_count" type="number" min="0" dir="ltr" /></label>
        <label class="ins-field"><span>بیشترین انتخاب</span><input v-model.number="fields.max_choice_count" type="number" min="0" dir="ltr" /></label>
        <label class="ins-field wide"><span>پیام خطای تعداد</span><input v-model="fields.choice_count_error_msg_fa" placeholder="مثلاً: حداکثر دو گزینه انتخاب کنید." /></label>
      </div>
      <p v-if="!capabilities.length" class="ins-note">این نوع پاسخ در اپ قانون جداگانه‌ای ندارد.</p>
    </template>
  </div>
</template>

<script>
import { KINDS, REGEX_PRESETS, describeRule, emptyRuleFields, fa } from '../designerModel';

const CAPABILITY_FIELDS = {
  value: ['min_value', 'max_value'], length: ['min_lenght', 'max_lenght'], regex: ['regex'], count: ['min_choice_count', 'max_choice_count'],
};

export default {
  name: 'RuleEditor',
  props: {
    rule: { type: Object, required: true },
    kind: { type: String, required: true },
    presets: { type: Array, required: true },
  },
  data: () => ({ sample: '', regexPresets: REGEX_PRESETS }),
  computed: {
    capabilities() { return (KINDS[this.kind] && KINDS[this.kind].rules) || []; },
    kindLabel() { return KINDS[this.kind] ? KINDS[this.kind].label : this.kind; },
    modes() {
      return [{ key: 'none', label: 'بدون قانون' }, { key: 'preset', label: 'قانون آماده' }, ...(this.capabilities.length ? [{ key: 'custom', label: 'قانون اختصاصی' }] : [])];
    },
    fields() { return this.rule.fields; },
    preset() { return this.presets.find(item => item.id === this.rule.preset); },
    presetSummary() { return this.preset ? describeRule(this.preset) : []; },
    // the preset sets something this input ignores (e.g. a regex on a number)
    presetIgnored() {
      if (!this.preset) return false;
      return Object.entries(CAPABILITY_FIELDS).some(([cap, names]) => !this.capabilities.includes(cap) && names.some(name => this.preset[name] != null && this.preset[name] !== ''));
    },
    regexError() {
      if (!this.fields.regex) return '';
      try { new RegExp(this.fields.regex); return ''; } catch (_) { return 'این الگو معتبر نیست.'; }
    },
    sampleOk() { try { return new RegExp(this.fields.regex).test(this.sample.trim()); } catch (_) { return false; } },
    valuePlaceholder() { return this.kind === 'Price' ? 'مثلاً: مبلغ بیش از حد مجاز است.' : 'مثلاً: عدد باید بین ۱ و ۱۰ باشد.'; },
  },
  created() { if (!this.rule.fields) this.$set(this.rule, 'fields', emptyRuleFields()); },
  methods: {
    fa,
    has(capability) { return this.capabilities.includes(capability); },
    setMode(mode) {
      if (mode === this.rule.mode) return;
      if (mode === 'custom' && this.rule.mode !== 'custom') this.rule.fields = emptyRuleFields();
      if (mode === 'preset' && !this.rule.preset) this.rule.preset = null;
      this.rule.mode = mode;
    },
    loadPreset() { if (this.preset) this.rule.fields = { ...emptyRuleFields(), ...pick(this.preset) }; },
    customize() { this.rule.fields = { ...emptyRuleFields(), ...(this.preset ? pick(this.preset) : {}) }; this.rule.preset = null; this.rule.mode = 'custom'; },
    useRegex(item) { this.fields.regex = item.regex; if (!this.fields.regex_error_msg_fa) this.fields.regex_error_msg_fa = item.message; },
  },
};

function pick(source) {
  return Object.keys(emptyRuleFields()).reduce((fields, name) => ({ ...fields, [name]: source[name] === undefined ? null : source[name] }), {});
}

</script>

<style scoped>
.rule { display: grid; gap: 10px; }
.tester small.ok { color: var(--teal-700); font-weight: 700; }
</style>
